import { useQueries } from '@tanstack/react-query';
import { postBatchQualifications } from 'services/api/AgentCenterAPI';
import { queryKeys } from '../queryKeys';
import { useMemo } from 'react';

const BATCH_SIZE = 100;

/**
 * Splits an array into chunks of specified size
 */
const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
};

export const usePostBatchQualifications = (callIds: string[]) => {
    // Split callIds into batches of 100
    const batches = useMemo(() => chunkArray(callIds, BATCH_SIZE), [callIds]);

    // Fetch all batches in parallel
    const queries = useQueries({
        queries: batches.map((batch) => ({
            queryKey: queryKeys.qualifications(batch),
            queryFn: () => postBatchQualifications(batch),
            enabled: batch.length > 0,
        })),
    });

    // Combine results from all batches
    const batchQualificationsData = useMemo(() => {
        if (queries.every((query) => !query.data)) {
            return undefined;
        }

        // Merge all qualifications from all batches
        const combinedQualifications: Record<string, any[]> = {};

        queries.forEach((query) => {
            if (query.data?.qualifications) {
                Object.entries(query.data.qualifications).forEach(([callId, qualifications]) => {
                    combinedQualifications[callId] = qualifications;
                });
            }
        });

        return {
            qualifications: combinedQualifications,
        };
    }, [queries]);

    // Check if any query is loading
    const isBatchQualificationsLoading = queries.some((query) => query.isLoading);

    // Check if any query has an error
    const hasError = queries.some((query) => query.isError);
    const error = queries.find((query) => query.isError)?.error;

    return {
        batchQualificationsData,
        isBatchQualificationsLoading,
        isError: hasError,
        error,
        refetch: () => {
            queries.forEach((query) => query.refetch());
        },
    };
};
